import type { KanbanMoveEvent, SelectOption } from "@atlas/core";
import { useToast } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IUiGroupMapping } from "@models";
import { mappingRowId } from "@models";
import {
  buildApplicationMappingSelectOptions,
  useApplicationGroups,
  useCompanyAppsMapping,
  useGroupMappings,
  useIdpGroups,
  useSaveGroupMappings,
} from "@query";
import { APP_BASE_PATH } from "@shared";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  filterGroupList,
  filterMappings,
  getGroupKanbanItemId,
  type IGroupKanbanItem,
  isMappingZoneKanbanItem,
  KANBAN_COLUMN_APP,
  KANBAN_COLUMN_IDP,
  KANBAN_COLUMN_MAPPING,
  MAPPING_ZONE_KANBAN_ITEM,
  type MappingFilterTab,
  mappingsEqual,
  toKanbanItem,
  toUiMappings,
} from "./applicationGroupMappingUtils";

function toSavePayload(list: IUiGroupMapping[]) {
  return list.map((mapping) => ({
    idpGroupId: mapping.idpGroupId,
    applicationGroupId: mapping.applicationGroupId,
    applicationGroupName: mapping.applicationGroupName,
  }));
}

export interface IUseApplicationGroupMappingParams {
  companyUuid: string | undefined;
  companyName?: string;
}

export function useApplicationGroupMapping({
  companyUuid,
  companyName,
}: IUseApplicationGroupMappingParams) {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: companyAppsMapping = [], isLoading: appsLoading } =
    useCompanyAppsMapping(companyUuid, APP_BASE_PATH);

  const applicationOptions = useMemo(
    () => buildApplicationMappingSelectOptions(companyAppsMapping),
    [companyAppsMapping],
  );

  const [selectedAppMapId, setSelectedAppMapId] = useState("");
  const [mappings, setMappings] = useState<IUiGroupMapping[]>([]);
  const [selectedAppGroupId, setSelectedAppGroupId] = useState<string | null>(
    null,
  );
  const [selectedIdpGroupId, setSelectedIdpGroupId] = useState<string | null>(
    null,
  );
  const [appSearch, setAppSearch] = useState("");
  const [idpSearch, setIdpSearch] = useState("");
  const [mappingSearch, setMappingSearch] = useState("");
  const [appFilter, setAppFilter] = useState<MappingFilterTab>("all");
  const [idpFilter, setIdpFilter] = useState<MappingFilterTab>("all");
  useEffect(() => {
    setSelectedAppMapId((current) => {
      if (
        current &&
        applicationOptions.some((opt) => opt.appMapId === current)
      ) {
        return current;
      }
      return applicationOptions[0]?.appMapId ?? "";
    });
  }, [applicationOptions]);

  const { data: applicationGroups = [], isLoading: appGroupsLoading } =
    useApplicationGroups(companyUuid, selectedAppMapId, APP_BASE_PATH);

  const { data: idpGroups = [], isLoading: idpGroupsLoading } = useIdpGroups(
    companyUuid,
    APP_BASE_PATH,
    { includeInactive: false },
  );

  const { data: persistedMappings = [], isLoading: mappingsLoading } =
    useGroupMappings(companyUuid, selectedAppMapId, APP_BASE_PATH);

  const { mutate: saveMappings } = useSaveGroupMappings(
    companyUuid,
    selectedAppMapId,
    APP_BASE_PATH,
    {
      onSuccess: (items) => {
        const ui = toUiMappings(items, applicationGroups, idpGroups);
        setMappings(ui);
        toast.add({
          title: t("hub.administer.mapping_save_success"),
          description: t("hub.administer.mapping_save_success_description"),
          data: { type: "success" },
        });
      },
      onError: () => {
        toast.add({
          title: t("hub.administer.mapping_save_error"),
          description: t("hub.administer.mapping_save_error_description"),
          data: { type: "error" },
        });
      },
    },
  );

  useEffect(() => {
    const ui = toUiMappings(persistedMappings, applicationGroups, idpGroups);
    setMappings((prev) => (mappingsEqual(prev, ui) ? prev : ui));
  }, [persistedMappings, applicationGroups, idpGroups]);

  useEffect(() => {
    setSelectedAppGroupId(null);
    setSelectedIdpGroupId(null);
    setAppSearch("");
    setIdpSearch("");
    setMappingSearch("");
    setAppFilter("all");
    setIdpFilter("all");
  }, [selectedAppMapId]);

  const mappedAppGroupIds = useMemo(
    () => new Set(mappings.map((m) => m.applicationGroupId)),
    [mappings],
  );

  const mappedIdpGroupIds = useMemo(
    () => new Set(mappings.map((m) => m.idpGroupId)),
    [mappings],
  );

  const filteredAppGroups = useMemo(
    () =>
      filterGroupList(
        applicationGroups,
        appSearch,
        appFilter,
        mappedAppGroupIds,
      ),
    [applicationGroups, appSearch, appFilter, mappedAppGroupIds],
  );

  const filteredIdpGroups = useMemo(
    () => filterGroupList(idpGroups, idpSearch, idpFilter, mappedIdpGroupIds),
    [idpGroups, idpSearch, idpFilter, mappedIdpGroupIds],
  );

  const filteredMappings = useMemo(
    () => filterMappings(mappings, mappingSearch),
    [mappings, mappingSearch],
  );

  const selectedAppGroup = applicationGroups.find(
    (g) => g.id === selectedAppGroupId,
  );
  const selectedIdpGroup = idpGroups.find((g) => g.id === selectedIdpGroupId);

  const persistMappings = useCallback(
    (nextMappings: IUiGroupMapping[], rollbackMappings: IUiGroupMapping[]) => {
      if (!companyUuid || !selectedAppMapId) return;
      setMappings(nextMappings);
      saveMappings(
        { mappings: toSavePayload(nextMappings) },
        {
          onError: () => {
            setMappings(rollbackMappings);
          },
        },
      );
    },
    [companyUuid, saveMappings, selectedAppMapId],
  );

  const applyAppMapSelection = useCallback((appMapId: string) => {
    setSelectedAppMapId(appMapId);
    setSelectedAppGroupId(null);
    setSelectedIdpGroupId(null);
    setAppSearch("");
    setIdpSearch("");
    setMappingSearch("");
    setAppFilter("all");
    setIdpFilter("all");
  }, []);

  const stats = useMemo(
    () => ({
      appTotal: applicationGroups.length,
      appMapped: mappedAppGroupIds.size,
      appUnmapped: applicationGroups.length - mappedAppGroupIds.size,
      idpTotal: idpGroups.length,
      idpMapped: mappedIdpGroupIds.size,
      idpUnmapped: idpGroups.length - mappedIdpGroupIds.size,
      activeMappings: mappings.length,
    }),
    [
      applicationGroups.length,
      idpGroups.length,
      mappedAppGroupIds.size,
      mappedIdpGroupIds.size,
      mappings.length,
    ],
  );

  const kanbanValue = useMemo<Record<string, IGroupKanbanItem[]>>(() => {
    return {
      [KANBAN_COLUMN_APP]: filteredAppGroups.map((g) => toKanbanItem(g, "app")),
      [KANBAN_COLUMN_MAPPING]: [MAPPING_ZONE_KANBAN_ITEM],
      [KANBAN_COLUMN_IDP]: filteredIdpGroups.map((g) => toKanbanItem(g, "idp")),
    };
  }, [filteredAppGroups, filteredIdpGroups]);

  const resolveKanbanItem = useCallback(
    (kanbanId: string): IGroupKanbanItem | undefined => {
      if (kanbanId === getGroupKanbanItemId(MAPPING_ZONE_KANBAN_ITEM)) {
        return MAPPING_ZONE_KANBAN_ITEM;
      }
      const items = [
        ...applicationGroups.map((g) => toKanbanItem(g, "app")),
        ...idpGroups.map((g) => toKanbanItem(g, "idp")),
      ];
      return items.find((item) => getGroupKanbanItemId(item) === kanbanId);
    },
    [applicationGroups, idpGroups],
  );

  const handleKanbanValueChange = useCallback(
    (_next: Record<string, IGroupKanbanItem[]>) => {},
    [],
  );

  const applySelectionFromItem = useCallback((item: IGroupKanbanItem) => {
    if (item.kind === "app") {
      setSelectedAppGroupId(item.id);
    } else {
      setSelectedIdpGroupId(item.id);
    }
  }, []);

  const handleKanbanMove = useCallback(
    (event: KanbanMoveEvent) => {
      const activeId = String(event.event.active.id);
      const overId = event.event.over ? String(event.event.over.id) : "";
      const activeItem = resolveKanbanItem(activeId);
      const overItem = overId ? resolveKanbanItem(overId) : undefined;

      if (!activeItem || isMappingZoneKanbanItem(activeItem)) return;

      const droppedOnMappingZone =
        event.overContainer === KANBAN_COLUMN_MAPPING ||
        (overItem !== undefined && isMappingZoneKanbanItem(overItem));

      if (droppedOnMappingZone) {
        applySelectionFromItem(activeItem);
        return;
      }

      if (activeItem.kind === "app") {
        if (
          event.overContainer === KANBAN_COLUMN_IDP ||
          overItem?.kind === "idp"
        ) {
          setSelectedAppGroupId(activeItem.id);
          if (overItem?.kind === "idp") {
            setSelectedIdpGroupId(overItem.id);
          }
          return;
        }
      }

      if (activeItem.kind === "idp") {
        if (
          event.overContainer === KANBAN_COLUMN_APP ||
          overItem?.kind === "app"
        ) {
          setSelectedIdpGroupId(activeItem.id);
          if (overItem?.kind === "app") {
            setSelectedAppGroupId(overItem.id);
          }
        }
      }
    },
    [applySelectionFromItem, resolveKanbanItem],
  );

  const handleCreateMapping = useCallback(() => {
    if (!selectedAppGroup || !selectedIdpGroup) return;

    const exists = mappings.some(
      (m) =>
        m.applicationGroupId === selectedAppGroup.id &&
        m.idpGroupId === selectedIdpGroup.id,
    );
    if (exists) {
      toast.add({
        title: t("hub.administer.mapping_exists_title"),
        description: t("hub.administer.mapping_exists_description"),
        data: { type: "warning" },
      });
      return;
    }

    const nextMappings: IUiGroupMapping[] = [
      ...mappings,
      {
        id: mappingRowId(selectedAppGroup.id, selectedIdpGroup.id),
        applicationGroupId: selectedAppGroup.id,
        applicationGroupName: selectedAppGroup.name,
        idpGroupId: selectedIdpGroup.id,
        idpGroupName: selectedIdpGroup.name,
      },
    ];
    setSelectedAppGroupId(null);
    setSelectedIdpGroupId(null);
    persistMappings(nextMappings, mappings);
  }, [mappings, persistMappings, selectedAppGroup, selectedIdpGroup, t, toast]);

  const handleRemoveMapping = useCallback(
    (mappingId: string) => {
      const nextMappings = mappings.filter((m) => m.id !== mappingId);
      persistMappings(nextMappings, mappings);
    },
    [mappings, persistMappings],
  );

  const applicationSelectOptions: SelectOption[] = useMemo(
    () =>
      applicationOptions.map((opt) => ({
        value: opt.appMapId,
        label: opt.label,
      })),
    [applicationOptions],
  );

  const loading =
    appsLoading || appGroupsLoading || idpGroupsLoading || mappingsLoading;

  const companyLabel =
    companyName || t("hub.administer.mapping_company_fallback");

  const handleApplicationSelectChange = useCallback(
    (value: string) => {
      applyAppMapSelection(value);
    },
    [applyAppMapSelection],
  );

  const toggleAppGroupSelection = useCallback((groupId: string) => {
    setSelectedAppGroupId((prev) => (prev === groupId ? null : groupId));
  }, []);

  const toggleIdpGroupSelection = useCallback((groupId: string) => {
    setSelectedIdpGroupId((prev) => (prev === groupId ? null : groupId));
  }, []);

  return {
    t,
    companyLabel,
    selectedAppMapId,
    applicationSelectOptions,
    appsLoading,
    loading,
    companyUuid,
    handleApplicationSelectChange,
    stats,
    kanbanValue,
    handleKanbanValueChange,
    handleKanbanMove,
    filteredAppGroups,
    filteredIdpGroups,
    filteredMappings,
    mappings,
    mappedAppGroupIds,
    mappedIdpGroupIds,
    selectedAppGroupId,
    selectedIdpGroupId,
    selectedAppGroup,
    selectedIdpGroup,
    appSearch,
    setAppSearch,
    idpSearch,
    setIdpSearch,
    mappingSearch,
    setMappingSearch,
    appFilter,
    setAppFilter,
    idpFilter,
    setIdpFilter,
    handleCreateMapping,
    handleRemoveMapping,
    toggleAppGroupSelection,
    toggleIdpGroupSelection,
  };
}
